/**
 * MCP-UI-1 — STDIO server form logic (extracted so it's unit-testable
 * outside the big AdminView shell).
 *
 * Backend (mcp_client._build_bifrost_payload) maps a STDIO server to Bifrost:
 *   command = command || "npx"
 *   args    = args || (package ? ["-y", package] : [])
 *   envs    = keys(config)            // env-var NAMES; values via credential
 *
 * So the four runtimes map as:
 *   npx    → send `package` (Bifrost builds `npx -y <package>`)
 *   uvx    → command "uvx",   args = [<package>, ...extra]
 *   docker → command "docker", args = [...user args, e.g. run --rm -i <image>]
 *   custom → command <binary>, args = [...user args]
 */

export const STDIO_RUNTIME_COMMAND = {
  npx: "npx",
  uvx: "uvx",
  docker: "docker",
  custom: "",
};

/** Parse a textarea of args — one argument per line, trimmed, blanks dropped. */
export function parseArgsText(text) {
  if (!text) return [];
  return String(text)
    .split("\n")
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

/**
 * Build the {package?, command?, args?} additions for a STDIO register payload
 * from the form state. Returns only the keys that apply to the chosen runtime.
 */
export function buildStdioServerFields(form) {
  const runtime = form.stdio_runtime || "npx";
  const extra = parseArgsText(form.args_text);
  if (runtime === "npx") {
    const out = {};
    if (form.package) out.package = form.package;
    return out;
  }
  if (runtime === "uvx") {
    const args = [form.package, ...extra].filter(Boolean);
    return { command: "uvx", args };
  }
  if (runtime === "docker") {
    return { command: "docker", args: extra };
  }
  // custom
  return { command: (form.command || "").trim(), args: extra };
}

/**
 * True when the form has enough to register a STDIO server for its runtime —
 * npx/uvx need a package, docker/custom need a command (docker's command is
 * fixed, so it only needs args → always allowed once server_id+name are set).
 */
export function stdioReady(form) {
  const runtime = form.stdio_runtime || "npx";
  if (runtime === "npx" || runtime === "uvx") return Boolean(form.package);
  if (runtime === "docker") return parseArgsText(form.args_text).length > 0;
  return Boolean((form.command || "").trim()); // custom
}

/**
 * Produce the mcpForm patch for a preset (from GET /api/mcp/stdio-presets).
 * Carries the suggested env-var keys + credential provider so the UI can
 * point the admin at creating the matching generic_env credential.
 */
export function applyStdioPreset(preset) {
  if (!preset) return {};
  return {
    connection_type: "stdio",
    stdio_runtime: preset.runtime || "npx",
    package: preset.package || "",
    command: "",
    args_text: (preset.args || []).join("\n"),
    presetEnvKeys: preset.env_keys || [],
    presetCredentialProvider: preset.credential_provider || null,
    presetHint: preset.hint || "",
  };
}
